import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"

import { ListItemStack } from "@molecules"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"
import ListItemDivider from "@molecules/ListItem/ListItemDivider/ListItemDivider"

import { Settings } from "@templates/index"

import { EstimatesNotificationTopics } from "@pages/Settings/SettingsNotifications/SettingsNotification.types"
import {
    notificationPreferenceQueryKey,
    useNotificationPreferencesQuery,
} from "@pages/Settings/SettingsNotifications/SettingsNotifications.store"
import NotificationProperty from "@pages/Settings/SettingsNotifications/components/NotificationProperty/NotificationProperty"

import { NOTIFICATION_ENDPOINTS } from "@endpoints/notification"

export default function EstimatesNotificationsForm() {
    const [shouldSetDefaultFormData, setShouldSetDefaultFormData] = useState<boolean>(false)

    const { topicPreference: estimatesNotificationPreference, isLoading: isLoadingData } =
        useNotificationPreferencesQuery<EstimatesNotificationTopics>("estimates")

    const queryClient = useQueryClient()

    useEffect(() => {
        if (!isLoadingData && !!estimatesNotificationPreference) {
            setShouldSetDefaultFormData(true)
        }
    }, [isLoadingData])

    const onMutationSuccess = () => {
        void queryClient.invalidateQueries({ queryKey: notificationPreferenceQueryKey })
    }

    const onAfterFormValuesSet = () => {
        setShouldSetDefaultFormData(false)
    }

    return (
        <GenericForm<EstimatesNotificationTopics, EstimatesNotificationTopics, Error, EstimatesNotificationTopics>
            formConfig={{
                defaultValues: undefined,
            }}
            mutationConfig={{
                genericErrorMessage: "Could not update notification preference",
                genericSuccessMessage: "Notification preference updated",
                method: "PATCH",
                endpoint: NOTIFICATION_ENDPOINTS.UPDATE_NOTIFICATION_PREFERENCES,
            }}
            onMutationSuccess={onMutationSuccess}
            formValues={shouldSetDefaultFormData ? estimatesNotificationPreference : undefined}
            onAfterFormValuesSet={onAfterFormValuesSet}
            isAutoSave={true}
        >
            {() => {
                return (
                    <Settings.NotificationConfigPage.Wrapper>
                        <ListItemStack>
                            <NotificationProperty<EstimatesNotificationTopics>
                                label="Estimate approved"
                                supportText="Notify me when a client approves an estimate."
                                emailCheckboxName="enable_estimate_notification_estimate_approved_email"
                                smsCheckboxName="enable_estimate_notification_estimate_approved_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<EstimatesNotificationTopics>
                                label="Estimate declined"
                                supportText="Notify me when a client declines an estimate."
                                emailCheckboxName="enable_estimate_notification_estimate_declined_email"
                                smsCheckboxName="enable_estimate_notification_estimate_declined_sms"
                                isLoading={isLoadingData}
                            />
                        </ListItemStack>
                    </Settings.NotificationConfigPage.Wrapper>
                )
            }}
        </GenericForm>
    )
}
